import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Wallet, ArrowLeft, ArrowRight } from "lucide-react";
import { usePlatform } from "@/context/PlatformContext";
import { useAuth } from "@/context/AuthContext";

export default function PublicHeader({ showBack = false, showNav = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = usePlatform();
  const { user } = useAuth();
  const appTitle = settings?.title || "GEMS";
  const isLoggedIn = Boolean(user && user.id);

  const handleHashClick = (e, hashId) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.getElementById(hashId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      navigate(`/#${hashId}`);
    }
  };

  const handleLogoClick = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-slate-200/60">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 lg:px-8 py-4">
        {/* Brand Logo & Title */}
        <Link
          to="/"
          onClick={handleLogoClick}
          className="flex items-center gap-2 cursor-pointer"
          data-testid="landing-logo"
        >
          <div className="w-9 h-9 rounded-2xl bg-slate-900 grid place-items-center overflow-hidden">
            <Wallet className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="heading text-lg font-bold tracking-tight text-slate-900">{appTitle}</div>
        </Link>

        {/* Center Nav Links */}
        {showNav && (
          <nav className="hidden sm:flex items-center gap-6 text-sm text-slate-600 font-normal">
            <a
              href="#features"
              onClick={(e) => handleHashClick(e, "features")}
              className="hover:text-slate-900 transition-colors"
            >
              Features
            </a>
            <a
              href="#how"
              onClick={(e) => handleHashClick(e, "how")}
              className="hover:text-slate-900 transition-colors"
            >
              How it works
            </a>
            <a
              href="#security"
              onClick={(e) => handleHashClick(e, "security")}
              className="hover:text-slate-900 transition-colors"
            >
              Ledger
            </a>
          </nav>
        )}

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          {showBack && (
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-full hover:bg-slate-100 transition-all mr-1"
              data-testid="public-header-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}

          {isLoggedIn ? (
            <Link
              to="/dashboard"
              className="text-sm font-semibold px-4 h-10 rounded-full bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-1.5 active:scale-95 transition-all shadow-xs"
              data-testid="landing-nav-cta"
            >
              <span>Go to Workspace</span>
              <ArrowRight className="w-4 h-4 text-emerald-400" />
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-slate-700 hover:text-slate-900 font-semibold px-3 py-2"
                data-testid="landing-signin-link"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="text-sm font-semibold px-4 h-10 rounded-full bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-1.5 active:scale-95 transition-all shadow-xs"
                data-testid="landing-nav-cta"
              >
                <span>Get started</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
